import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, PhoneCall, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { bookCounselling } from "../lib/api";

export const Counselling = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && form.phone.trim().length >= 10;

  const waHref = `https://wa.me/917850911096?text=${encodeURIComponent(
    `Hi Sanvalio Academy! I'm ${form.name} and I'd like a free mentor counselling call.\nPhone: ${form.phone}${form.email ? `\nEmail: ${form.email}` : ""}${form.interest ? `\nI want to learn: ${form.interest}` : ""}`
  )}`;

  const submit = async () => {
    setBusy(true);
    try {
      await bookCounselling(form);
      setDone(true);
      toast.success("Counselling call booked!");
      window.open(waHref, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not book right now. Please WhatsApp us instead.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-void/60 px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-600 outline-none focus:border-gold/60 transition-colors";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-[16px] p-4"
        onClick={onClose}
        data-testid="counselling-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-md rounded-3xl border border-white/10 bg-elevated p-7 sm:p-9"
          data-testid="counselling-modal"
        >
          <button
            data-testid="counselling-close-button"
            onClick={onClose}
            className="absolute right-5 top-5 rounded-full border border-white/15 p-2 text-neutral-400 hover:text-champagne hover:border-gold/50 transition-colors"
          >
            <X size={16} />
          </button>

          {done ? (
            <div className="py-6 text-center" data-testid="counselling-success">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 border border-gold/40">
                <Check size={26} className="text-gold" />
              </div>
              <h3 className="mt-6 font-display text-2xl sm:text-3xl text-neutral-100">You're booked!</h3>
              <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
                Your free live counselling session is confirmed. Our mentor team will call you on your number shortly — keep your questions ready.
              </p>
              <a
                data-testid="counselling-whatsapp-button"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3 text-sm font-semibold text-void hover:bg-[#3ee07a] transition-colors"
              >
                <MessageCircle size={16} /> Send Details on WhatsApp — Fastest
              </a>
              <p className="mt-4 text-xs text-neutral-500">
                Or call us now at <span className="text-champagne">+91 78509 11096</span>
              </p>
            </div>
          ) : (
            <div data-testid="counselling-form">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.15em] text-gold/90">Free · 1-on-1 · Live</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl text-neutral-100">Talk to a mentor</h3>
              <p className="mt-3 text-sm font-light text-neutral-400 leading-relaxed">
                A free counselling call with the Sanvalio Academy team — pick the right course, plan your career, zero pressure.
              </p>
              <div className="mt-7 space-y-4">
                <input data-testid="counselling-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                <input data-testid="counselling-phone-input" className={inputCls} placeholder="Phone (WhatsApp)" value={form.phone} onChange={set("phone")} />
                <input data-testid="counselling-email-input" className={inputCls} placeholder="Email (optional)" type="email" value={form.email} onChange={set("email")} />
                <input data-testid="counselling-interest-input" className={inputCls} placeholder="What do you want to learn? (optional)" value={form.interest} onChange={set("interest")} />
              </div>
              <button
                data-testid="counselling-submit-button"
                disabled={!valid || busy}
                onClick={submit}
                className="btn-shine mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-leaf px-6 py-3.5 text-sm font-semibold text-void shadow-[0_10px_30px_-10px_rgba(232,193,90,0.55)] transition-all hover:bg-mint disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <PhoneCall size={15} />}
                {busy ? "Booking…" : "Book My Free Call"}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
